import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { FeatureFlag } from '@ndvr/auth-api';
import { MeetingTranscriptView, NoteTemplateType, NoteView } from '@ndvr/customer-api';

import { CustomerApi } from 'API';
import { useAuthContext } from 'views/auth/Auth';
import { useFeatureFlag } from 'views/auth/useFeatureFlag';
import { Toast } from 'widgets';

export const NoteQueryKeys = {
    NotesQueryKey: (householdGUID: string, eventGUID?: string) => ['Notes', householdGUID, eventGUID] as const,
    NotesByEventQueryKey: (eventGUID: string) => ['NotesByEvent', eventGUID] as const,
    NoteQueryKey: (noteGUID: string) => ['Note', noteGUID] as const,
    NoteTemplateTypesQueryKey: () => ['NoteTemplateTypes'] as const,
    MeetingBotQueryKey: (meetingBotGUID: string) => ['MeetingBot', meetingBotGUID] as const,
    MeetingTranscriptQueryKey: (meetingBotGUID: string) => ['MeetingTranscript', meetingBotGUID] as const,
} as const;

export const useNotesQuery = (householdGUID: string, eventGUID?: string) =>
    useQuery<NoteView[]>({
        enabled: !!householdGUID,
        queryKey: NoteQueryKeys.NotesQueryKey(householdGUID, eventGUID),
        queryFn: async () => {
            const [response] = await CustomerApi().listNotes({ householdGUID, eventGUID });
            return response || [];
        },
    });

export const useNotesByEventQuery = (eventGUID: string | undefined) =>
    useQuery<NoteView[]>({
        enabled: !!eventGUID,
        queryKey: NoteQueryKeys.NotesByEventQueryKey(eventGUID!),
        queryFn: async () => {
            const [response] = await CustomerApi().listNotesByEvent(eventGUID!);
            return response || [];
        },
    });

export const useNoteQuery = (noteGUID: string) => {
    return useQuery<NoteView | null>({
        enabled: !!noteGUID,
        queryKey: NoteQueryKeys.NoteQueryKey(noteGUID),
        queryFn: async () => {
            const [response] = await CustomerApi().getNote(noteGUID);
            return response;
        },
    });
};

/**
 * Redacted meeting transcript behind the CC_SHOW_MEETING_TRANSCRIPT feature flag.
 * Resolves to null when the flag is off, the note has no meeting bot, or no transcript is stored.
 */
export const useMeetingTranscript = (meetingBotGUID: string | null | undefined) => {
    const isTranscriptEnabled = useFeatureFlag(FeatureFlag.CC_SHOW_MEETING_TRANSCRIPT);

    const transcriptQuery = useQuery<MeetingTranscriptView | null>({
        enabled: !!meetingBotGUID && !!isTranscriptEnabled,
        queryKey: NoteQueryKeys.MeetingTranscriptQueryKey(meetingBotGUID ?? ''),
        queryFn: async () => {
            const [response] = await CustomerApi({ hideError: true }).getMeetingTranscript(meetingBotGUID!);
            return response ?? null;
        },
    });

    return transcriptQuery.data?.transcript ?? null;
};

export const useAddNoteMutation = (householdGUID: string) => {
    const queryClient = useQueryClient();
    const { user } = useAuthContext();

    return useMutation({
        mutationFn: async (data: { subject: string; content: string; householdGUID: string; eventGUID?: string }) => {
            const response = await CustomerApi().createNote(data.householdGUID, {
                subject: data.subject,
                content: data.content,
                eventGUID: data.eventGUID,
            });
            return response;
        },
        onSuccess: (_data, variables) => {
            queryClient.invalidateQueries({
                queryKey: NoteQueryKeys.NotesQueryKey(variables.householdGUID),
            });
            if (variables.eventGUID) {
                queryClient.invalidateQueries({
                    queryKey: NoteQueryKeys.NotesByEventQueryKey(variables.eventGUID),
                });
            }
            // The calendar Notes reading list derives its has-note cards from the calendar
            // views payload, so a new note must refresh the calendar queries too.
            queryClient.invalidateQueries({ queryKey: ['personOrg', user?.personOrgGUID, 'calendar'] });
        },
    });
};

export const usePinNoteMutation = (householdGUID: string | undefined) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: { noteGUID: string; pinned: boolean }) => {
            await CustomerApi().updateNotePinned(data.noteGUID, { pinned: data.pinned });
        },
        onSuccess: (_data, variables) => {
            if (householdGUID) {
                queryClient.invalidateQueries({
                    queryKey: NoteQueryKeys.NotesQueryKey(householdGUID),
                });
            }
            queryClient.invalidateQueries({
                queryKey: NoteQueryKeys.NoteQueryKey(variables.noteGUID),
            });
            queryClient.invalidateQueries({ queryKey: ['NotesByEvent'] });
        },
        onError: (e: unknown) => {
            Toast.forError(e, 'Failed to update note pin.');
        },
    });
};

export const useDeleteNoteMutation = (householdGUID: string | undefined) => {
    const queryClient = useQueryClient();
    const { user } = useAuthContext();
    return useMutation({
        mutationFn: async (noteGUID: string) => {
            const response = await CustomerApi().deleteNote(noteGUID);
            return response;
        },
        onSuccess: () => {
            if (householdGUID) {
                queryClient.invalidateQueries({
                    queryKey: NoteQueryKeys.NotesQueryKey(householdGUID),
                });
            }
            queryClient.invalidateQueries({ queryKey: ['NotesByEvent'] });
            // The calendar Notes reading list derives its has-note cards from the calendar
            // views payload, so a deleted note must refresh the calendar queries too.
            queryClient.invalidateQueries({ queryKey: ['personOrg', user?.personOrgGUID, 'calendar'] });
        },
    });
};

export const useUpdateNoteMutation = (householdGUID: string, noteGUID: string) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (data: { subject: string; content: string; noteGUID: string }) => {
            const response = await CustomerApi().updateNote(data.noteGUID, {
                subject: data.subject,
                content: data.content,
            });
            return response;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: NoteQueryKeys.NotesQueryKey(householdGUID),
            });
            queryClient.invalidateQueries({
                queryKey: NoteQueryKeys.NoteQueryKey(noteGUID),
            });
            queryClient.invalidateQueries({ queryKey: ['NotesByEvent'] });
        },
    });
};

export const useCreatePortfolioFromTranscript = (householdGUID: string | undefined, noteGUID: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (meetingBotGUID: string) => {
            await CustomerApi({ hideError: true }).createPortfolioFromTranscript(meetingBotGUID);
            return true;
        },
        onSuccess: () => {
            Toast.success('Portfolio created from meeting transcript.');
            queryClient.invalidateQueries({
                queryKey: NoteQueryKeys.NoteQueryKey(noteGUID),
            });
            if (householdGUID) {
                queryClient.invalidateQueries({
                    queryKey: NoteQueryKeys.NotesQueryKey(householdGUID),
                });
            }
            queryClient.invalidateQueries({ queryKey: ['NotesByEvent'] });
        },
        onError: (e: unknown) => {
            Toast.forError(e, 'Failed to create portfolio from transcript.');
        },
    });
};

export const useApplyTemplateToNoteMutation = (householdGUID: string | undefined, noteGUID: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (templateType: NoteTemplateType) => {
            await CustomerApi().applyTemplateToNote(noteGUID, { noteTemplateType: templateType });
        },
        onSuccess: () => {
            Toast.success('Template applied successfully.');
            queryClient.invalidateQueries({
                queryKey: NoteQueryKeys.NoteQueryKey(noteGUID),
            });
            if (householdGUID) {
                queryClient.invalidateQueries({
                    queryKey: NoteQueryKeys.NotesQueryKey(householdGUID),
                });
            }
            queryClient.invalidateQueries({ queryKey: ['NotesByEvent'] });
        },
        onError: (e: unknown) => {
            Toast.forError(e, 'Failed to apply template to note.');
        },
    });
};
